import React from 'react';

/** True when the notes editor has visible text or embedded media (not just empty tags / `<br>`). */
export function noteHtmlHasContent(html: string): boolean {
  const trimmed = String(html ?? '').trim();
  if (!trimmed) return false;
  if (typeof document === 'undefined') {
    return trimmed.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').trim().length > 0;
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = trimmed;
  if ((tmp.textContent ?? '').replace(/\u00a0/g, ' ').trim()) return true;
  return Boolean(tmp.querySelector('img, video, iframe'));
}

function isBlockBoundary(node: Node): boolean {
  if (!(node instanceof HTMLElement)) return false;
  const tag = node.tagName;
  return (
    tag === 'BR' ||
    tag === 'DIV' ||
    tag === 'P' ||
    tag === 'LI' ||
    tag === 'UL' ||
    tag === 'OL' ||
    tag === 'BLOCKQUOTE'
  );
}

function isInsideList(node: Node, editor: HTMLElement): boolean {
  let n: Node | null = node;
  while (n && n !== editor) {
    if (n instanceof HTMLElement && (n.tagName === 'UL' || n.tagName === 'OL' || n.tagName === 'LI')) {
      return true;
    }
    n = n.parentNode;
  }
  return false;
}

function rangeFromLineStartToCaret(editor: HTMLElement, caret: Range): Range {
  const lineRange = caret.cloneRange();
  const container = caret.startContainer;

  if (container.nodeType === Node.TEXT_NODE) {
    const before = (container.textContent ?? '').slice(0, caret.startOffset);
    const nl = before.lastIndexOf('\n');
    if (nl >= 0) {
      lineRange.setStart(container, nl + 1);
      return lineRange;
    }

    let prev: Node | null = container.previousSibling;
    while (prev) {
      if (isBlockBoundary(prev)) {
        lineRange.setStartAfter(prev);
        return lineRange;
      }
      prev = prev.previousSibling;
    }

    const parent = container.parentNode;
    if (parent && parent !== editor && parent instanceof HTMLElement && isBlockBoundary(parent)) {
      lineRange.setStart(parent, 0);
      return lineRange;
    }

    lineRange.setStart(container, 0);
    return lineRange;
  }

  if (container instanceof HTMLElement) {
    lineRange.setStart(container, 0);
    return lineRange;
  }

  lineRange.setStart(editor, 0);
  return lineRange;
}

function getCurrentListItem(node: Node | null, editor: HTMLElement): HTMLLIElement | null {
  let n: Node | null = node;
  while (n && n !== editor) {
    if (n instanceof HTMLLIElement) return n;
    n = n.parentNode;
  }
  return null;
}

function listItemIsEmpty(li: HTMLLIElement): boolean {
  if (li.querySelector('img, video, iframe')) return false;
  return !(li.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

function caretIsAtStartOf(el: HTMLElement, sel: Selection): boolean {
  if (!sel.rangeCount || !sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  const pre = range.cloneRange();
  try {
    pre.selectNodeContents(el);
    pre.setEnd(range.startContainer, range.startOffset);
  } catch {
    return false;
  }
  return !pre.toString().replace(/\u00a0/g, ' ').trim();
}

function placeCaretIn(el: HTMLElement): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Pull the current `li` out of its list into a following block. */
function unwrapListItem(li: HTMLLIElement): HTMLElement {
  const list = li.parentElement;
  if (!list) return li;
  const parent = list.parentNode;
  if (!parent) return li;

  const block = document.createElement('div');
  while (li.firstChild) {
    const child = li.firstChild;
    const emptyBr =
      child instanceof HTMLBRElement &&
      !child.nextSibling &&
      !(li.textContent ?? '').replace(/\u00a0/g, ' ').trim();
    if (emptyBr) {
      li.removeChild(child);
      continue;
    }
    block.appendChild(child);
  }
  if (!block.firstChild) block.appendChild(document.createElement('br'));

  const restItems: Node[] = [];
  while (li.nextSibling) restItems.push(li.removeChild(li.nextSibling));
  li.remove();

  const restList =
    restItems.length > 0
      ? (() => {
          const nextList = list.cloneNode(false) as HTMLElement;
          for (const node of restItems) nextList.appendChild(node);
          return nextList;
        })()
      : null;

  if (list.childNodes.length === 0) {
    parent.replaceChild(block, list);
  } else {
    parent.insertBefore(block, list.nextSibling);
  }
  if (restList) parent.insertBefore(restList, block.nextSibling);
  return block;
}

function exitEmptyListItem(editor: HTMLElement, li: HTMLLIElement): boolean {
  editor.focus();
  const sel = window.getSelection();
  if (sel && document.execCommand('outdent')) {
    const still = sel.anchorNode ? getCurrentListItem(sel.anchorNode, editor) : null;
    if (!still || still !== li) return true;
  }
  placeCaretIn(unwrapListItem(li));
  return true;
}

/** Enter on an empty list item leaves the list (undoable via `outdent` when the browser supports it). */
export function tryExitEmptyListItem(editor: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel?.anchorNode || !editor.contains(sel.anchorNode)) return false;
  const li = getCurrentListItem(sel.anchorNode, editor);
  if (!li || !listItemIsEmpty(li)) return false;
  return exitEmptyListItem(editor, li);
}

/** Backspace at the start of an empty list item unwraps it. */
export function tryUnwrapListOnBackspace(editor: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel?.anchorNode || !editor.contains(sel.anchorNode) || !sel.isCollapsed) return false;
  const li = getCurrentListItem(sel.anchorNode, editor);
  if (!li || !listItemIsEmpty(li) || !caretIsAtStartOf(li, sel)) return false;
  return exitEmptyListItem(editor, li);
}

/** Converts a leading `-` / `*` into a `<ul>`, or `1.` / `1)` into an `<ol>`. Uses execCommand so Ctrl+Z undoes it. */
export function tryConvertMarkdownListAtCaret(editor: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return false;
  const anchor = sel.anchorNode;
  if (!anchor || !editor.contains(anchor)) return false;
  if (isInsideList(anchor, editor)) return false;

  const caret = sel.getRangeAt(0);
  const lineRange = rangeFromLineStartToCaret(editor, caret);
  const prefix = lineRange.toString().replace(/\u00a0/g, ' ').trimEnd();
  const unordered = prefix === '-' || prefix === '*';
  const ordered = /^\d+[.)]$/.test(prefix);
  if (!unordered && !ordered) return false;

  editor.focus();
  sel.removeAllRanges();
  sel.addRange(lineRange);
  const html = ordered ? '<ol><li><br></li></ol>' : '<ul><li><br></li></ul>';
  if (document.execCommand('insertHTML', false, html)) return true;

  lineRange.deleteContents();
  const list = document.createElement(ordered ? 'ol' : 'ul');
  const li = document.createElement('li');
  li.appendChild(document.createElement('br'));
  list.appendChild(li);
  lineRange.insertNode(list);
  placeCaretIn(li);
  return true;
}

export function NotesToolbarButton({
  onCommand,
  value,
  icon: Icon,
  title,
  onClick,
  editorRef,
  savedRangeRef,
}: {
  onCommand?: string;
  value?: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  title: string;
  onClick?: () => void;
  editorRef?: React.RefObject<HTMLDivElement | null>;
  savedRangeRef?: React.MutableRefObject<Range | null>;
}) {
  const doCommand = (e: React.MouseEvent) => {
    if (editorRef?.current && savedRangeRef) {
      const s = window.getSelection();
      if (s?.rangeCount && editorRef.current.contains(s.anchorNode)) {
        savedRangeRef.current = s.getRangeAt(0).cloneRange();
      }
    }
    e.preventDefault();
    const sel = window.getSelection();
    const range = savedRangeRef?.current ?? (sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null);
    const inEditor = editorRef?.current && range && editorRef.current.contains(range.commonAncestorContainer);
    if (editorRef?.current) {
      editorRef.current.focus();
      if (inEditor && range) {
        try {
          sel?.removeAllRanges();
          sel?.addRange(range);
        } catch (_) {}
      }
    }
    if (onClick) {
      onClick();
      return;
    }
    if (onCommand) {
      document.execCommand(onCommand, false, value ?? undefined);
    }
  };
  return (
    <button
      type="button"
      onMouseDown={doCommand}
      title={title}
      className="p-1.5 rounded text-app-text-muted hover:bg-slate-200 hover:text-app-text"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

export function insertListInNotes(
  editorRef: React.RefObject<HTMLDivElement | null>,
  savedRangeRef: React.MutableRefObject<Range | null>,
  ordered: boolean,
) {
  const editor = editorRef.current;
  if (!editor) return;
  const sel = window.getSelection();
  if (!sel) return;
  editor.focus();
  let range = savedRangeRef.current?.cloneRange();
  if (!range || !editor.contains(range.commonAncestorContainer)) {
    range = null;
    if (sel.rangeCount && editor.contains(sel.anchorNode)) range = sel.getRangeAt(0).cloneRange();
  }
  if (range) {
    try {
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (_) {}
  }
  const doInsert = () => {
    const r = range?.cloneRange();
    if (!r || !editor.contains(r.commonAncestorContainer)) return;
    const tag = ordered ? 'ol' : 'ul';
    const list = document.createElement(tag);
    const li = document.createElement('li');
    const selectedText = (sel.toString() || '').trim();
    li.textContent = selectedText || '\u00A0';
    list.appendChild(li);
    try {
      r.deleteContents();
      r.insertNode(list);
      const newRange = document.createRange();
      newRange.setStart(li, 0);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } catch (_) {}
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(doInsert);
  });
}
